import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { _SalesOrderItem, _single } from "../../../../../core/services/dataBean/EntityCatalog";
import useDataBean from "../../../../../core/services/dataBean/useDataBean";
import { SelectProductEditor } from "../../../../Factoty&Production/ManufacturingStandars/Components/SelectProductEditor";
import { CurrencyEditor } from "../../../../erp/cellEditor/Currency/CurrencyEditor";
import { MeasurementUnitCodeEditor } from "../../../../erp/MeasurementUnitCode/MeasurementUnitCode.editor";
import { BasicButton } from "../../../../theme/Component/BasicButton";
import { BasicTable, MASK_DECIMAL, MASK_MONEY, MASK_NUMBER } from "../../../../theme/Component/BasicTable";
import { ListMenu } from "../../../../theme/Component/ListMenu";
import { Modal } from "../../../../theme/Component/Modal";
import { ModalConfirm } from "../../../../theme/Component/ModalConfirm";
import useModal, { MODAL_LG, MODAL_md } from "../../../../theme/Component/hooks/useModal";
import usePaginator from "../../../../theme/Component/usePaginator";

interface Props {
    idSalesOrder: number;
    salesOrderDate: any;
    isModal?: boolean;
    _onChange?: (value: any) => void;
}

interface FilterProps {
    IDProduct?: number;
    ProductDescription?: string;
    RegexStr?: string;
}

const RECORDS_PER_PAGE = 15;

export const SalesOrderItem = ({ idSalesOrder, salesOrderDate, isModal = false, _onChange }: Props) => {
    const {
        instance,
        lista,
        setLista,
        bean,
        setBean,
        addRequiredFields,
        validateForm,
        isRequired,
        isMissing,
    } = useDataBean(_SalesOrderItem);
    const paginator = usePaginator();
    const [filter, setFilter] = useState<FilterProps>({ RegexStr: "" });
    const [submitted, setSubmitted] = useState(false);

    const modalForm = useModal("Producto de la oportunidad de venta", MODAL_LG);
    const modalDelete = useModal("Eliminar producto de la oportunidad", MODAL_md);

    useEffect(() => {
        addRequiredFields([
            "Description",
            "Quantity",
            "TotalItemAmount",
            "IDCurrency",
            "IDMeasurementUnitCode",
        ]);
    }, []);

    useEffect(() => {
        getLista();
    }, [idSalesOrder, salesOrderDate, paginator.page]);

    const getLista = () => {
        if (idSalesOrder == null || salesOrderDate == null) {
            setLista([]);
            return;
        }

        instance.getSalesOrderItemCatalog(
            idSalesOrder,
            salesOrderDate,
            filter.IDProduct,
            filter.RegexStr,
            paginator.page,
            RECORDS_PER_PAGE
        ).then((resp: any) => {
            paginator.setPages(resp?.NumberOfPages ?? 0);
            paginator.setRecords((resp?.NumberOfPages ?? 0) * RECORDS_PER_PAGE);
            paginator.setRegiPerPage(RECORDS_PER_PAGE);
            setLista(resp?.List ?? []);
        }).catch((err: any) => toast.error(err));
    };

    const search = () => {
        if (paginator.page !== 1) {
            paginator.setPage(1);
        } else {
            getLista();
        }
    };

    const openCreate = () => {
        setSubmitted(false);
        setBean({
            IDSalesOrder: idSalesOrder,
            SalesOrderDate: salesOrderDate,
            Description: "",
            Quantity: null,
            UnitaryValue: null,
            TotalItemAmount: null,
            IDCurrency: null,
            IDMeasurementUnitCode: null,
            IDProduct: null,
        });
        modalForm.open("Agregar producto a la oportunidad");
    };

    const openEdit = () => {
        setSubmitted(false);
        modalForm.open(`Editar producto ${bean.IDSalesOrderItem}`);
    };

    const saveItem = () => {
        setSubmitted(true);
        const hasInvalidSelectors = !bean.IDCurrency || !bean.IDMeasurementUnitCode;

        if (!validateForm() || hasInvalidSelectors) {
            toast.error("Faltan campos obligatorios por completar");
            return;
        }

        instance.updateSalesOrderItem({
            ...bean,
            IDSalesOrder: idSalesOrder,
            SalesOrderDate: salesOrderDate,
        }).then(() => {
            toast.success(bean.IDSalesOrderItem ? "Producto actualizado correctamente" : "Producto agregado correctamente");
            modalForm.close();
            getLista();
        }).catch((err: any) => toast.error(err));
    };

    const deleteItem = () => {
        instance.deleteSalesOrderItem(salesOrderDate, bean.IDSalesOrderItem).then(() => {
            toast.success("Producto eliminado correctamente");
            modalDelete.close();
            getLista();
        }).catch((err: any) => toast.error(err));
    };

    const selectProduct = (product: any) => {
        const unitaryValue = product.SalesValue ?? bean.UnitaryValue;
        const quantity = bean.Quantity;

        setBean({
            ...bean,
            IDProduct: product.IDProduct,
            ProductDescription: product.Description,
            Description: bean.Description || product.Description || "",
            UnitaryValue: unitaryValue,
            TotalItemAmount: quantity != null && unitaryValue != null
                ? Number(quantity) * Number(unitaryValue)
                : bean.TotalItemAmount,
        });
    };

    const updateQuantity = (value: string) => {
        const quantity = value === "" ? null : Number(value);
        setBean({
            ...bean,
            Quantity: quantity,
            TotalItemAmount: quantity != null && bean.UnitaryValue != null
                ? quantity * Number(bean.UnitaryValue)
                : bean.TotalItemAmount,
        });
    };

    const head = [
        { title: "ID", property: "IDSalesOrderItem", visible: true, mask: MASK_NUMBER },
        { title: "Descripción", property: "Description", visible: true },
        { title: "Cantidad", property: "Quantity", visible: true, mask: MASK_DECIMAL },
        { title: "Valor Unitario", property: "UnitaryValue", visible: true, mask: MASK_MONEY },
        { title: "Valor Total", property: "TotalItemAmount", visible: true, mask: MASK_MONEY },
        { title: "Moneda", property: "CurrencyName", visible: true },
        { title: "Unidad de Medida", property: "MeasurementUnitCodeName", visible: true },
        { title: "Producto", property: "ProductDescription", visible: true },
    ];

    const rowButtons = (
        <ListMenu listaBotones={[
            { titulo: "Editar", icono: "ri-edit-2-line", visible: _single.canEdit(), evento: openEdit },
            { titulo: "Eliminar", icono: "ri-delete-bin-line", visible: _single.canDelete(), evento: () => modalDelete.open() },
        ]} />
    );

    const setValue = (value: any) => {
        setBean(value);
        _onChange?.(value);
    };

    return (
        <>
            <div className="row">
                <div className="col-md-6">
                    <SelectProductEditor
                        label="Producto"
                        value={filter.IDProduct}
                        renderValue={filter.ProductDescription}
                        onChange={(product: any) => setFilter({
                            ...filter,
                            IDProduct: product.IDProduct,
                            ProductDescription: product.Description,
                        })}
                    />
                </div>
                <div className="col-md-6">
                    <label>Búsqueda por Texto</label>
                    <input
                        className="form-control"
                        value={filter.RegexStr ?? ""}
                        onChange={(e) => setFilter({ ...filter, RegexStr: e.target.value })}
                        onKeyDown={(e) => e.key === "Enter" && search()}
                    />
                </div>
                <div className="col-md-12 mt-4 p-2">
                    <BasicButton icon="ri-search-line" eventClick={search}>Buscar</BasicButton>
                </div>
                <div className="col-md-12">
                    <BasicTable
                        body={lista}
                        head={head}
                        rowSet={setValue}
                        listButtons={isModal ? undefined : rowButtons}
                        iconButtons={isModal ? undefined : [{
                            nombre: "Agregar",
                            visible: _single.canCreate(),
                            icono: "ri-file-add-line",
                            evento: openCreate,
                        }]}
                        records={paginator}
                    />
                </div>
            </div>


            <Modal modal={modalForm.modal} updateModal={modalForm.setModal} eventModal={saveItem}>
                <div className="row">
                    <div className="col-md-12">
                        <label>Producto</label>
                        <SelectProductEditor
                            label=" "
                            value={bean.IDProduct}
                            renderValue={bean.ProductDescription}
                            onChange={selectProduct}
                        />
                    </div>
                    <div className="col-md-12">
                        <label className={submitted && isMissing("Description") ? "has-error" : ""}>
                            Descripción {isRequired("Description") ? <span className="text-danger">*</span> : ""}
                        </label>
                        <textarea
                            className="form-control"
                            value={bean.Description ?? ""}
                            onChange={(e) => setBean({ ...bean, Description: e.target.value })}
                        />
                    </div>
                    <div className="col-md-6">
                        <label className={submitted && isMissing("Quantity") ? "has-error" : ""}>
                            Cantidad {isRequired("Quantity") ? <span className="text-danger">*</span> : ""}
                        </label>
                        <input
                            className="form-control"
                            type="number"
                            value={bean.Quantity ?? ""}
                            onChange={(e) => updateQuantity(e.target.value)}
                        />
                    </div>
                    <div className="col-md-6">
                        <label>Valor Unitario</label>
                        <input className="form-control" type="number" value={bean.UnitaryValue ?? ""} readOnly />
                    </div>
                    <div className="col-md-6">
                        <label className={submitted && isMissing("TotalItemAmount") ? "has-error" : ""}>
                            Valor Total {isRequired("TotalItemAmount") ? <span className="text-danger">*</span> : ""}
                        </label>
                        <input
                            className="form-control"
                            type="number"
                            value={bean.TotalItemAmount ?? ""}
                            onChange={(e) => setBean({
                                ...bean,
                                TotalItemAmount: e.target.value === "" ? null : Number(e.target.value),
                            })}
                        />
                    </div>
                    <div className={`col-md-6 ${submitted && (isMissing("IDCurrency") || !bean.IDCurrency) ? "has-error" : ""}`}>
                        <CurrencyEditor
                            id={bean.IDCurrency}
                            onchange={(value: number) => setBean({ ...bean, IDCurrency: value })}
                        />
                    </div>
                    <div className="col-md-12">
                        <label className={submitted && (isMissing("IDMeasurementUnitCode") || !bean.IDMeasurementUnitCode) ? "has-error" : ""}>
                            Unidad de Medida {isRequired("IDMeasurementUnitCode") ? <span className="text-danger">*</span> : ""}
                        </label>
                        <MeasurementUnitCodeEditor
                            label=" "
                            idSelector={bean.IDMeasurementUnitCode}
                            onChange={(value: any) => setBean({ ...bean, IDMeasurementUnitCode: Number(value) })}
                        />
                    </div>
                </div>
            </Modal>

            <ModalConfirm modal={modalDelete.modal} updateModal={modalDelete.setModal} eventModal={deleteItem}>
                <strong>Eliminar producto {bean.Description}</strong>
            </ModalConfirm>
        </>
    );
};
